import { DataTable, TableMenu } from '@/components'
import { cn } from '@/lib/utils'
import type { ResultNode } from '@/types/util'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { subSeconds } from 'date-fns'
import { useMemo, useRef } from 'react'
import { Link } from 'react-router'
import type { USER_ABSENCES_QUERY } from '../queries'
import type { AbsenceTypeNode } from '../types.graphql'
import type { AbsenceDialogRef } from './absence-dialog'
import { AbsenceDialog } from './absence-dialog'
import { useOldAbsenceHandler } from './handler.hooks'
import { AbsenceTypeHeader, DateCell, StatusCell } from './table-cells'
import { PERMISSIONS, usePermissions } from '@/utils'

export type AbsenceNode = ResultNode<typeof USER_ABSENCES_QUERY, 'allAbsences'>

interface Props {
  absences: AbsenceNode[]

  loading: boolean
  showEmployee: boolean
  selectedAbsenceType: Pick<AbsenceTypeNode, 'id' | 'name'> | null

  onSelectAbsenceType: (
    absenceType: Pick<AbsenceTypeNode, 'id' | 'name'> | null
  ) => void
}

export function AbsencesTable({
  absences,

  loading,
  showEmployee,
  selectedAbsenceType,

  onSelectAbsenceType,
}: Props) {
  const translations = useTranslate({
    link: 'common.link',
    employee: 'common.employee',
    dayStart: 'common.day-start',
    dayEnd: 'common.day-end',
    absenceType: 'timesheets.absence-type',
    reason: 'common.reason',
    status: 'common.status',

    open: 'common.open',
    items: ['absences.table.num-items', { n: 0 }],
  })

  const absenceDialogRef = useRef<AbsenceDialogRef>(null)
  const absenceHandler = useOldAbsenceHandler(absenceDialogRef)

  const { hasPermissions } = usePermissions()
  const canAddOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.add.othersabsence,
    false
  )

  const column = createColumnHelper<AbsenceNode>()

  const tableColumns = useMemo(
    () => [
      column.display({
        id: 'link',
        header: translations.link,
        meta: {
          narrow: true,
        },
        cell(info) {
          const { id } = info.row.original

          return (
            <Link className="hover:text-file-onedrive" to={`/absences/${id}`}>
              {translations.open}
            </Link>
          )
        },
      }),
      column.accessor('user', {
        header: translations.employee,
        meta: {
          narrow: true,
        },
        enableSorting: true,
        cell(info) {
          const user = info.getValue()

          return (
            <Link
              className="text-inherit"
              to={`/users/${user.id}`}
              target="_blank"
            >
              {user.fullName}
            </Link>
          )
        },
      }),
      column.accessor('datetimeStart', {
        header: translations.dayStart,
        meta: {
          narrow: true,
        },
        enableSorting: true,
        sortingFn: (a, b) =>
          new Date(a.original.datetimeStart).getTime() -
          new Date(b.original.datetimeStart).getTime(),
        cell(info) {
          const startDate = new Date(info.getValue())
          return <DateCell date={startDate} side="start" type="absence" />
        },
      }),
      column.accessor('datetimeEnd', {
        header: translations.dayEnd,
        meta: {
          narrow: true,
          end: true,
        },
        enableSorting: true,
        sortingFn: (a, b) =>
          new Date(a.original.datetimeEnd).getTime() -
          new Date(b.original.datetimeEnd).getTime(),
        cell(info) {
          const endDate = subSeconds(new Date(info.getValue()), 1)
          return <DateCell date={endDate} side="end" type="absence" />
        },
      }),
      column.accessor('absenceType', {
        enableSorting: true,
        sortingFn: (a, b) => {
          const aName = a.original.absenceType?.name || ''
          const bName = b.original.absenceType?.name || ''

          return aName.localeCompare(bName)
        },
        header: () => {
          return (
            <AbsenceTypeHeader
              absenceType={selectedAbsenceType}
              onRemove={() => onSelectAbsenceType(null)}
            >
              {translations.absenceType}
            </AbsenceTypeHeader>
          )
        },
        meta: { narrow: true },
        cell(info) {
          const absenceType = info.getValue()
          if (!absenceType) return '-'

          return (
            <div
              className={cn(
                'cursor-pointer',
                !!selectedAbsenceType
                  ? 'hover:text-red-500'
                  : 'hover:text-secondary'
              )}
              role="button"
              onClick={() =>
                onSelectAbsenceType(!!selectedAbsenceType ? null : absenceType)
              }
            >
              {absenceType.name}
            </div>
          )
        },
      }),
      column.accessor('reason', {
        header: translations.reason,
        cell(info) {
          const reason = info.getValue()
          return reason
        },
      }),
      column.accessor('requestStatus', {
        header: translations.status,
        meta: { narrow: true },
        enableSorting: true,
        cell(info) {
          const requestStatus = info.getValue()
          return <StatusCell status={requestStatus} />
        },
      }),
      column.display({
        id: 'menu',
        header: '',
        meta: { narrow: true, center: true },
        cell(info) {
          const absence = info.row.original
          const items = absenceHandler.createAbsenceMenuItems(absence, true)

          return <TableMenu items={items} />
        },
      }),
    ],
    [
      absenceHandler,
      column,
      onSelectAbsenceType,
      selectedAbsenceType,
      translations.absenceType,
      translations.dayEnd,
      translations.dayStart,
      translations.employee,
      translations.link,
      translations.open,
      translations.reason,
      translations.status,
    ]
  )

  return (
    <>
      <AbsenceDialog
        ref={absenceDialogRef}
        hideTrigger
        canAddForOthers={canAddOthersAbsence}
      />

      <DataTable
        columns={tableColumns}
        data={absences}
        pagination={{
          itemsText: n => translations.items({ n }),
        }}
        hiddenColumns={{
          user: showEmployee,
        }}
        initialSort={[{ id: 'datetimeStart', desc: true }]}
        inCard
        loading={loading}
      />
    </>
  )
}
